// src/components/FloatingSupportButton.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Send, Lightbulb, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../api/api";

const telegramLink  = "https://t.me/larrydigitals";
const communityLink = "https://chat.whatsapp.com/J7ZTawEOmLw85ZFm9qOfbL";
const mailLink      = "mailto:contactus@larrydigitals.com";

export default function FloatingSupportButton() {
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = () => {
      setOpen(false);
      setShowForm(true);
    };
    window.addEventListener("open-suggestion-form", handler);
    return () => window.removeEventListener("open-suggestion-form", handler);
  }, []);

  const openForm = () => {
    setOpen(false);
    setShowForm(true);
  };

  const closeForm = () => {
    if (sending) return;
    setShowForm(false);
  };

  const submitSuggestion = async () => {
    if (sending) return;
    if (message.trim().length < 5) {
      toast.error("Please write a little more before sending.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/suggestions", {
        message: message.trim(),
        contact: contact.trim(),
        page: window.location.pathname,
      });
      toast.success(res.data?.message || "Thank you! Your suggestion has been sent.");
      setMessage("");
      setContact("");
      setShowForm(false);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.message || "Too many suggestions. Please try again later.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-20 md:bottom-4 right-3 z-50 flex flex-col items-end gap-2">

        {open && (
          <div className="mb-1 w-52 rounded-xl overflow-hidden shadow-2xl border border-gray-700">

            {/* Purple header */}
            <div className="flex items-center gap-2 bg-purple-600 px-3 py-2.5">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="h-3.5 w-3.5 fill-white">
                  <path d="M256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm0 110c23.2 0 42 18.8 42 42s-18.8 42-42 42-42-18.8-42-42 18.8-42 42-42zm56 254h-112v-18c0-4.4 3.6-8 8-8h8v-96h-8c-4.4 0-8-3.6-8-8v-18h80v122h8c4.4 0 8 3.6 8 8v18z"/>
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white leading-tight truncate">LarryDigitals Support</p>
                <p className="text-[10px] text-white/80 flex items-center gap-1 mt-0.5">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400 shrink-0"></span>
                  Online — replies in minutes
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="bg-gray-900 px-2.5 py-2.5 flex flex-col gap-1.5">
              <p className="text-[10px] text-gray-400 mb-0.5">How can we help you today?</p>

              {/* Chat with Us (Telegram) */}
              <a
                href={telegramLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-2 hover:bg-gray-700 transition no-underline"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-700">
                  <Send size={14} className="text-purple-400" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white leading-tight">Chat with Us</p>
                  <p className="text-[10px] text-gray-400 truncate">Telegram — fastest response</p>
                </div>
              </a>

              {/* Join Community */}
              <a
                href={communityLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-2 hover:bg-gray-700 transition no-underline"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-700">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" className="h-3.5 w-3.5 fill-purple-400">
                    <path d="M96 224c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64zm448 0c35.3 0 64-28.7 64-64s-28.7-64-64-64-64 28.7-64 64 28.7 64 64 64zm32 32h-64c-17.6 0-33.5 7.1-45.1 18.6 40.3 22.1 68.9 62 75.1 109.4h66c17.7 0 32-14.3 32-32v-32c0-35.3-28.7-64-64-64zm-256 0c61.9 0 112-50.1 112-112S381.9 32 320 32 208 82.1 208 144s50.1 112 112 112zm76.8 32h-8.3c-20.8 10-43.9 16-68.5 16s-47.6-6-68.5-16h-8.3C179.6 288 128 339.6 128 403.2V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-28.8c0-63.6-51.6-115.2-115.2-115.2zm-223.7-13.4C185.5 263.1 169.6 256 152 256H88c-35.3 0-64 28.7-64 64v32c0 17.7 14.3 32 32 32h65.9c6.3-47.4 34.9-87.3 75.2-109.4z"/>
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white leading-tight">Join Community</p>
                  <p className="text-[10px] text-gray-400 truncate">WhatsApp group — tips &amp; updates</p>
                </div>
              </a>

              {/* Send a suggestion */}
              <button
                type="button"
                onClick={openForm}
                className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-2 hover:bg-gray-700 transition text-left w-full"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-700">
                  <Lightbulb size={14} className="text-purple-400" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-white leading-tight">Send a suggestion</p>
                  <p className="text-[10px] text-gray-400 truncate">Help us improve</p>
                </div>
              </button>

              {/* Mail Us */}
              <a
                href={mailLink}
                className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-2.5 py-2 hover:bg-gray-700 transition no-underline"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-700">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="h-3.5 w-3.5 fill-purple-400">
                    <path d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"/>
                  </svg>
                </span>
                <div className="min-w-0 overflow-hidden">
                  <p className="text-xs font-semibold text-white leading-tight">Mail Us</p>
                  <p className="text-[10px] text-gray-400 truncate">contactus@larrydigitals.com</p>
                </div>
              </a>

            </div>
          </div>
        )}

        {/* Main toggle button */}
        <button
          onClick={() => setOpen((v) => !v)}
          className={`flex h-12 w-12 items-center justify-center rounded-full shadow-2xl transition-all active:scale-95 ${
            open ? "bg-gray-700" : "bg-purple-600 hover:bg-purple-700"
          }`}
          aria-label="Support"
        >
          {open ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="h-5 w-5 fill-white">
              <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="h-5 w-5 fill-white">
              <path d="M512 240c0 114.9-114.6 208-256 208c-37.1 0-72.3-6.4-104.1-17.9c-11.9 4.8-35.8 16.5-83 24.8c-14.4 2.5-26.9-10.1-24.1-24.4c4.5-24.7 10.3-52.5 11.4-63.9C20.2 330.5 0 287.1 0 240C0 125.1 114.6 32 256 32s256 93.1 256 208z"/>
            </svg>
          )}
        </button>
      </div>

      {/* Suggestion modal */}
      {showForm &&
        createPortal(
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 p-3"
            style={{ zIndex: 2147483000 }}
            onClick={closeForm}
          >
            <div
              className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 border border-black/5 dark:border-white/10 shadow-2xl p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/40">
                    <Lightbulb size={16} className="text-purple-600 dark:text-purple-300" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Send a suggestion</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                      Tell us what we can do better.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  aria-label="Close"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
                >
                  <X size={16} />
                </button>
              </div>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={1000}
                rows={5}
                placeholder="Write your suggestion here..."
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 px-3 py-2.5 outline-none focus:ring-2 focus:ring-purple-500/40 resize-none"
              />
              <p className="text-[10px] text-gray-400 text-right mt-1">{message.length}/1000</p>

              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                maxLength={120}
                placeholder="Email or phone (optional, if you want a reply)"
                className="mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 px-3 py-2.5 outline-none focus:ring-2 focus:ring-purple-500/40"
              />

              <button
                type="button"
                onClick={submitSuggestion}
                disabled={sending}
                className="mt-4 w-full rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-semibold py-3 transition"
              >
                {sending ? "Sending..." : "Send suggestion"}
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}